import { useEffect, useRef, useState, type TransitionEvent } from 'react';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components';
import { Spinner } from '@/components/Spinner';
import { cn } from '@/lib/utils';
import {
    FullSource,
    ReadyStage,
    SourceStatus,
    SourceType,
} from '@/types/source';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    getSourceStatusLabel,
    isAnalyzingPhaseSourceStatus,
} from '../sourceStatusDisplay';

const READY_VISIBLE_MS = 10000;
const READY_EXIT_FALLBACK_MS = 500;

interface SourcePageStatusBadgeProps {
    source: FullSource;
}

export function SourcePageStatusBadge({ source }: SourcePageStatusBadgeProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const shouldReduceMotion = useReducedMotion();
    const [readyStage, setReadyStage] = useState<ReadyStage>('off');
    const previousSourceIdRef = useRef(source.id);
    const previousStatusRef = useRef<SourceStatus | undefined>(undefined);

    useEffect(() => {
        if (previousSourceIdRef.current !== source.id) {
            previousSourceIdRef.current = source.id;
            previousStatusRef.current = source.status;
            setReadyStage('off');
            return;
        }

        const previousStatus = previousStatusRef.current;
        previousStatusRef.current = source.status;

        if (source.type === SourceType.CAMERA) {
            return;
        }

        const justCompleted =
            source.status === SourceStatus.COMPLETED &&
            previousStatus !== undefined &&
            previousStatus !== SourceStatus.COMPLETED;

        if (justCompleted) {
            setReadyStage('on');
        }
    }, [source.id, source.status, source.type]);

    useEffect(() => {
        if (readyStage === 'on') {
            const timerId = window.setTimeout(
                () => setReadyStage('exit'),
                READY_VISIBLE_MS,
            );
            return () => window.clearTimeout(timerId);
        }
        if (readyStage === 'exit') {
            if (shouldReduceMotion) {
                setReadyStage('off');
                return;
            }
            const timerId = window.setTimeout(
                () => setReadyStage('off'),
                READY_EXIT_FALLBACK_MS,
            );
            return () => window.clearTimeout(timerId);
        }
    }, [readyStage, shouldReduceMotion]);

    const isCamera = source.type === SourceType.CAMERA;

    const handleReadyCelebrationTransitionEnd = (
        event: TransitionEvent<HTMLDivElement>,
    ) => {
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.propertyName !== 'max-height') {
            return;
        }
        setReadyStage('off');
    };

    if (isCamera && source.status !== SourceStatus.ERROR) {
        return null;
    }

    const showAnalyzingProgress =
        !isCamera && isAnalyzingPhaseSourceStatus(source.status);

    const showReadyCelebration =
        !isCamera &&
        source.status === SourceStatus.COMPLETED &&
        (readyStage === 'on' || readyStage === 'exit');

    const showIntermediateFileBadge =
        !isCamera &&
        source.status !== SourceStatus.COMPLETED &&
        source.status !== SourceStatus.ERROR;

    const showErrorBadge = source.status === SourceStatus.ERROR;

    const showBadge =
        showReadyCelebration || showIntermediateFileBadge || showErrorBadge;

    if (!showBadge) {
        return null;
    }

    const badgeVariant =
        source.status === SourceStatus.ERROR ? 'negative' : 'base';
    const badgeLabel =
        source.status === SourceStatus.COMPLETED
            ? t('status.analyzeComplete')
            : getSourceStatusLabel(source.status, t);
    const badgeValue = showAnalyzingProgress
        ? `${badgeLabel} (${source.progress}%)`
        : badgeLabel;

    const badgeBody = (
        <Badge
            variant={badgeVariant}
            className={badgeVariant === 'base' ? 'text-white-100' : undefined}
        >
            {showReadyCelebration && (
                <CheckCircleIcon
                    weight="fill"
                    className="size-3 shrink-0 text-success-100"
                    aria-hidden
                />
            )}
            {showAnalyzingProgress && (
                <Spinner size={10} variant="light" className="shrink-0" />
            )}
            <span className="text-xs-medium">{badgeValue}</span>
        </Badge>
    );

    if (showReadyCelebration) {
        return (
            <div
                className={cn(
                    'w-fit max-w-full overflow-hidden motion-reduce:transition-none',
                    'ease-[cubic-bezier(0.33,0,0.2,1)] duration-[380ms]',
                    'transition-[max-height,margin-bottom,opacity]',
                    readyStage === 'exit'
                        ? 'pointer-events-none mb-0 max-h-0 opacity-0'
                        : 'mb-3 max-h-14 opacity-100',
                )}
                onTransitionEnd={handleReadyCelebrationTransitionEnd}
            >
                {badgeBody}
            </div>
        );
    }

    return <div className="mb-3 w-fit max-w-full">{badgeBody}</div>;
}
